import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { handleNotification } from 'src/helpers/notifications';
import { useAppDispatch } from 'src/hooks/store';
import {
  addMemberToOrganization,
  fetchOrganization,
  removeOrganizationMemberAsAdmin,
  updateOrganizationMemberRoleAsAdmin,
} from 'src/slices/organization';
import { User } from 'src/types/auth.type';
import {
  Organization,
  OrganizationMember,
  OrganizationMemberRole,
} from 'src/types/organization.type';
import SearchFormField from '../forms/SearchFormField';

interface MemberDialogProps {
  members: OrganizationMember[];
  organization: Organization;
  open: boolean;
  onClose: () => void;
  users: User[];
}

export const OrganizationMemberDialog: React.FC<MemberDialogProps> = ({
  members,
  organization,
  open,
  onClose,
  users,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [deleteMode, setDeleteMode] = React.useState(false);
  const [selectedToDelete, setSelectedToDelete] = React.useState<string[]>([]);
  const [editingRoleId, setEditingRoleId] = React.useState<string | null>(null);
  const [newRole, setNewRole] = React.useState('');

  const [userSearch, setUserSearch] = React.useState('');
  const [selectedToAdd, setSelectedToAdd] = React.useState<string[]>([]);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const roles: { value: OrganizationMemberRole; label: string }[] = [
    {
      value: OrganizationMemberRole.owner,
      label: t('organization.fields.role.options.organization_owner.label'),
    },
    {
      value: OrganizationMemberRole.admin,
      label: t('organization.fields.role.options.organization_admin.label'),
    },
    {
      value: OrganizationMemberRole.member,
      label: t('organization.fields.role.options.organization_member.label'),
    },
  ];

  const filteredUsers = users.filter(
    (user) =>
      `${user.firstName} ${user.lastName} ${user.email}`
        .toLowerCase()
        .includes(userSearch.toLowerCase()) &&
      !members.some((m) => m._id === user._id),
  );

  const filteredMembers = members.filter((member) =>
    `${member.firstName} ${member.lastName}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );

  const handleDeleteMembers = () => {
    if (selectedToDelete.length > 0) {
      selectedToDelete.forEach((memberId) => {
        handleNotification(
          dispatch(
            removeOrganizationMemberAsAdmin({
              organizationId: organization._id,
              memberId,
            }),
          ),
          t('pages.Admin.successDeleteMember'),
          t('pages.Admin.errorDeleteMember'),
          enqueueSnackbar,
          closeSnackbar,
        );
      });
    }
    setDeleteMode(false);
    setSelectedToDelete([]);
  };

  const handleRoleChange = async (
    memberId: string,
    role: OrganizationMemberRole,
  ) => {
    await handleNotification(
      dispatch(
        updateOrganizationMemberRoleAsAdmin({
          memberId,
          organizationId: organization._id,
          dto: { role },
        }),
      ),
      t('pages.Admin.successUpdateUser'),
      t('pages.Admin.errorUpdateUser'),
      enqueueSnackbar,
      closeSnackbar,
    );
    await dispatch(fetchOrganization(organization._id));
    setEditingRoleId(null);
    setNewRole('');
  };

  const handleAddSelectedMembers = async () => {
    await handleNotification(
      dispatch(
        addMemberToOrganization({
          organizationId: organization._id,
          userIds: selectedToAdd,
        }),
      ),
      t('pages.Admin.successAddMember'),
      t('pages.Admin.errorAddMember'),
      enqueueSnackbar,
      closeSnackbar,
    );
    await dispatch(fetchOrganization(organization._id));
    setAddDialogOpen(false);
    setUserSearch('');
    setSelectedToAdd([]);
  };
  const maxLicenses = (organization.subscription?.maxNumberOfUsers ?? 0) + 1;
  const isOverLicenseLimit =
    members.length + selectedToAdd.length > maxLicenses;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {t('organization.fields.organizationsMembersList', {
          organizationName: organization?.name,
        })}
      </DialogTitle>
      <DialogContent
        sx={{ width: '700px', height: '700px', overflowY: 'hidden' }}
      >
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box flexGrow={1}>
              <SearchFormField
                placeholderKey={t(
                  'pages.Dashboard.updateVictimeRolesDialog.search',
                )}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                sx={{ width: '100%' }}
              />
            </Box>
            {!deleteMode && (
              <>
                <Tooltip title={t('pages.Admin.members.add')}>
                  <IconButton
                    onClick={() => setAddDialogOpen(true)}
                    size="small"
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('pages.Admin.members.delete')}>
                  <IconButton onClick={() => setDeleteMode(true)} size="small">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {deleteMode && (
              <>
                <Tooltip title={t('common.delete')}>
                  <span>
                    <IconButton
                      color="success"
                      onClick={handleDeleteMembers}
                      disabled={selectedToDelete.length === 0}
                      size="small"
                    >
                      <CheckIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('common.cancel')}>
                  <IconButton
                    color="error"
                    onClick={() => {
                      setDeleteMode(false);
                      setSelectedToDelete([]);
                    }}
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
          <List>
            {filteredMembers.map((member) => (
              <ListItem key={member._id} sx={{ alignItems: 'flex-start' }}>
                {deleteMode && (
                  <Checkbox
                    checked={selectedToDelete.includes(member._id)}
                    onChange={(e) => {
                      setSelectedToDelete((prev) =>
                        e.target.checked
                          ? [...prev, member._id]
                          : prev.filter((id) => id !== member._id),
                      );
                    }}
                  />
                )}
                <Box>
                  <Typography fontWeight="bold">
                    {`${member.firstName} ${member.lastName}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {member.email}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    alignSelf: 'center',
                    marginLeft: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {editingRoleId === member._id ? (
                    <>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={newRole || member.role}
                          onChange={(e) =>
                            setNewRole(e.target.value as OrganizationMemberRole)
                          }
                          size="small"
                          autoFocus
                        >
                          {roles.map((role) => (
                            <MenuItem key={role.value} value={role.value}>
                              {role.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Tooltip title={t('common.confirm')}>
                        <IconButton
                          color="success"
                          sx={{ ml: 1 }}
                          onClick={() =>
                            handleRoleChange(
                              member._id,
                              (newRole as OrganizationMemberRole) ||
                                member.role,
                            )
                          }
                        >
                          <CheckIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.cancel')}>
                        <IconButton
                          color="error"
                          sx={{ ml: 1 }}
                          onClick={() => {
                            setEditingRoleId(null);
                            setNewRole('');
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Typography
                        color="primary"
                        sx={{ cursor: 'pointer', mr: 1 }}
                      >
                        {t(
                          `organization.fields.role.options.${member.role}.label`,
                        )}
                      </Typography>
                      <Tooltip title={t('pages.Admin.members.editRole')}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingRoleId(member._id);
                            setNewRole(member.role);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>
        </Stack>
      </DialogContent>
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
        <DialogTitle>{t('pages.Admin.members.add')}</DialogTitle>
        <DialogContent
          sx={{ height: '400px', width: '500px', overflowY: 'auto' }}
        >
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: 'background.paper',
              p: 0.1,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <TextField
                label={t('pages.Admin.users.search')}
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                fullWidth
              />
              <Tooltip
                title={
                  isOverLicenseLimit
                    ? t('pages.Admin.members.licenseLimitExceeded', {
                        max: maxLicenses,
                      })
                    : ''
                }
                arrow
                disableHoverListener={!isOverLicenseLimit}
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleAddSelectedMembers}
                    disabled={selectedToAdd.length === 0 || isOverLicenseLimit}
                    sx={{
                      whiteSpace: 'nowrap',
                      height: 40,
                      border: isOverLicenseLimit ? '2px solid red' : undefined,
                      color: isOverLicenseLimit ? 'red' : undefined,
                    }}
                  >
                    {t('common.add')}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Box>
          <List>
            {filteredUsers.map((user) => (
              <ListItem key={user._id} disableGutters sx={{ py: 0.5 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Box>
                    <Typography fontWeight="bold" fontSize={15}>
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontSize={13}
                    >
                      {user.email}
                    </Typography>
                  </Box>
                  <Checkbox
                    checked={selectedToAdd.includes(user._id)}
                    onChange={(e) => {
                      setSelectedToAdd((prev) =>
                        e.target.checked
                          ? [...prev, user._id]
                          : prev.filter((id) => id !== user._id),
                      );
                    }}
                    sx={{ ml: 1 }}
                  />
                </Stack>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
